import { useCallback, useEffect, useState } from 'react'
import { api } from './api'
import type { FieldBody } from './fields'
import type { Field, FieldNote } from './types'

/** One field plus its notes, with the mutations the detail page needs. */
export function useField(orgId: string | undefined, farmId: string | undefined, fieldId: string | undefined) {
  const [field, setField] = useState<Field | null>(null)
  const [notes, setNotes] = useState<FieldNote[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const base = orgId && farmId && fieldId ? `/orgs/${orgId}/farms/${farmId}/fields/${fieldId}` : null

  const refresh = useCallback(async () => {
    if (!base) return
    setLoading(true)
    setError(null)
    try {
      const [loaded, loadedNotes] = await Promise.all([api.get<Field>(base), api.get<FieldNote[]>(`${base}/notes`)])
      setField(loaded)
      setNotes(loadedNotes)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load field.')
    } finally {
      setLoading(false)
    }
  }, [base])

  useEffect(() => {
    refresh()
  }, [refresh])

  async function updateField(update: FieldBody) {
    if (!base) return
    setField(await api.patch<Field>(base, update))
  }

  async function addNote(body: string) {
    if (!base) return
    const created = await api.post<FieldNote>(`${base}/notes`, { body })
    setNotes((prev) => [...prev, created])
  }

  async function archiveField() {
    if (!base) return
    await api.delete(base)
  }

  return { field, notes, loading, error, updateField, addNote, archiveField }
}
